import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async'; 
import Slider from "react-slick";
import {
  Typography,
  Box,
  Container,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Chip
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BuildIcon from '@mui/icons-material/Build';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ChatIcon from '@mui/icons-material/Chat';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import HandymanIcon from '@mui/icons-material/Handyman';
import ConstructionIcon from '@mui/icons-material/Construction';

const HomePage = () => {
  const navigate = useNavigate();

  const FeatureCard = ({ icon, title, description }) => (
    <Card
      component={motion.div}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
      sx={{
        height: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        background: 'linear-gradient(to bottom, white, #f9f9f9)',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ color: 'primary.main', mb: 2 }}>
          {icon}
        </Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Add React Helmet for SEO */}
      <Helmet>
        <title>DIYMind – AI for Smarter DIY Projects</title>
        <meta name="description" content="DIYMind is your AI-powered DIY assistant. Get smart video search, personalized project plans, and expert chatbot help for your next home project." />
        {/* Add structured data for brand recognition */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "DIYMind",
              "url": "https://diymind.ai",
              "logo": "https://diymind.ai/logo.png",
              "description": "AI-powered DIY assistant for home projects and renovations",
              "sameAs": []
            }
          `}
        </script>
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white',
          pt: 12,
          pb: 8,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                {/* Updated headline with brand name */}
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 'bold',
                    mb: 3
                  }}
                >
                  Transform Your DIY Dreams with DIYMind
                </Typography>
                <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
                  AI-powered video search and personalized project planning with expert virtual assistants
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/projects')}
                    startIcon={<SearchIcon />}
                    sx={{
                      bgcolor: 'white',
                      color: '#1e3c72',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.9)',
                      }
                    }}
                  >
                    Start Searching
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/personal-projects')}
                    startIcon={<PersonIcon />}
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.9)',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      }
                    }}
                  >
                    Choose an Expert
                  </Button>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Paper
                  elevation={6}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: 400,
                    borderRadius: 2,
                    overflow: 'hidden',
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <Box
                    component="img"
                    src="tools.jpg"
                    alt="DIYMind Project Planning"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
  
<Box sx={{ py: 8, px: 2, background: '#f4f6fa' }}>
  <Container maxWidth="md">
    <Typography
      variant="h4"
      textAlign="center"
      sx={{
        mb: 4,
        fontWeight: 'bold',
        background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        color: 'transparent',
        WebkitTextFillColor: 'transparent',
      }}
    >
      Explore DIY Blog Posts
    </Typography>

    <Slider
      dots={true}
      infinite={true}
      autoplay={true}
      autoplaySpeed={3000}
      speed={600}
      slidesToShow={3}
      slidesToScroll={1}
      arrows={false}
      responsive={[
        { breakpoint: 960, settings: { slidesToShow: 2 } },
        { breakpoint: 600, settings: { slidesToShow: 1 } }
      ]}
    >
      {[
        {
          title: 'Dresser Restoration',
          image: '/generated_image_dresser_vantity4.png',
          description: 'Restore and re-purpose a vintage dresser into a bathroom vanity',
          link: '/blog/restoration_artisan_vinatage_dresser_vanity'
        },
        {
          title: 'Garage Wall Organization',
          image: '/generated_image_modular_garage.png',
          description: 'Organize your garage tools with a smart, modular wall system.',
          link: '/blog/house_doctor_garage_wall_tool_organization'
        },
        {
          title: 'Sliding Barn Door Guide',
          image: '/generated_image_sliding_bsrn_kitchen_door.png',
          description: 'Modern style meets function with this DIY barn door.',
          link: '/blog/everyday_woodsmith_slding_barn_door'
        }
      ].map((post, i) => (
        <Box
          key={i}
          component={motion.div}
          whileHover={{ y: -5, scale: 1.02 }}
          sx={{
            px: 2,
            cursor: 'pointer'
          }}
          onClick={() => {
            navigate(post.link);
            window.scrollTo(0, 0);
          }}
        >
          <Card
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              background: 'white',
              height: '100%'
            }}
          >
            <Box
              component="img"
              src={post.image}
              alt={post.title}
              sx={{
                width: '100%',
                height: 160,
                objectFit: 'cover'
              }}
            />
            <Box sx={{ p: 2 }}>
              <Typography variant="h6">{post.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {post.description}
              </Typography>
            </Box>
          </Card>
        </Box>
      ))}
    </Slider>
  </Container>
</Box>
      {/* Main Features */}
      <Container sx={{ py: 8 }}>
        <Typography
          variant="h2"
          textAlign="center"
          sx={{
            mb: 6,
            fontWeight: 'bold',
            background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
            backgroundClip: 'text',
            textFillColor: 'transparent'
          }}
        >
          Three Powerful DIYMind AI Solutions
        </Typography>

        <Grid container spacing={4}>
          {/* Video Search Feature */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                height: '100%',
                borderRadius: 2,
                background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)'
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                >
                  <PlayCircleOutlineIcon sx={{ fontSize: 40 }} />
                  Smart Video Search
                </Typography>
                <Typography variant="body1" paragraph>
                  Find exactly what you need with our AI-powered video search engine.
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FeatureCard
                    icon={<SearchIcon sx={{ fontSize: 30 }} />}
                    title="Content-Based Search"
                    description="Our AI analyzes video content to find exact materials, tools, and steps for your project."
                  />
                </Grid>
                <Grid item xs={12}>
                  <FeatureCard
                    icon={<SmartToyIcon sx={{ fontSize: 30 }} />}
                    title="Interactive Chatbot"
                    description="Ask follow-up questions about any project detail and get instant, accurate answers."
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Personal Assistant Feature */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                height: '100%',
                borderRadius: 2,
                background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)'
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                >
                  <PersonIcon sx={{ fontSize: 40 }} />
                  Expert Personas
                </Typography>
                <Typography variant="body1" paragraph>
                  Get specialized guidance from AI experts to create comprehensive project plans and answer your questions.
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FeatureCard
                    icon={<DescriptionIcon sx={{ fontSize: 30 }} />}
                    title="Personalized Blueprints"
                    description="Receive customized step-by-step instructions, material lists, and budget estimates for your specific project."
                  />
                </Grid>
                <Grid item xs={12}>
                  <FeatureCard
                    icon={<ChatIcon sx={{ fontSize: 30 }} />}
                    title="Project Consultation"
                    description="Chat with your chosen expert about your project for personalized recommendations and advice."
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* DIY Tools Feature Section */}
      <Box sx={{ py: 8, bgcolor: '#f8f9fa' }}>
        <Container>
          <Typography
            variant="h2"
            textAlign="center"
            sx={{
              mb: 6,
              fontWeight: 'bold',
              background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
              backgroundClip: 'text',
              textFillColor: 'transparent'
            }}
          >
            Find the Perfect Tools with DIYMind
          </Typography>
          
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1e3c72' }}>
                  AI-Powered Tool Recommendations
                </Typography>
                <Typography variant="body1" paragraph>
                  Get personalized recommendations for the exact tools you need for your DIY projects. Our AI analyzes your project requirements and suggests the most suitable tools.
                </Typography>
                
                <Box sx={{ mt: 4 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: 1
                        }}
                      >
                        <HandymanIcon sx={{ fontSize: 36, color: '#1e3c72' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Project-Based
                        </Typography>
                        <Typography variant="body2">
                          Tools recommended specifically for your unique project needs
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: 1
                        }}
                      >
                        <ConstructionIcon sx={{ fontSize: 36, color: '#1e3c72' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Expert Guidance
                        </Typography>
                        <Typography variant="body2">
                          Understand why each tool is recommended for your specific task
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
                
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/tools')}
                  startIcon={<HandymanIcon />}
                  sx={{
                    mt: 4,
                    background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
                    px: 4
                  }}
                >
                  Find DIY Tools
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                sx={{
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Paper
                  elevation={4}
                  sx={{
                    p: 4,
                    borderRadius: 2,
                    width: '100%',
                    maxWidth: 500,
                    bgcolor: 'white',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#1e3c72', mb: 3 }}>
                    Sample Tool Recommendations
                  </Typography>
                  
                  {/* Sample tool card */}
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      mb: 2,
                      borderLeft: '4px solid #1e3c72'
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Cordless Power Drill
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Essential for your deck building project. Features adjustable torque settings and quick-change chuck for versatile use.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Chip size="small" label="Power Tool" sx={{ bgcolor: '#e3f2fd', color: '#1e3c72' }} />
                      <Button size="small" variant="outlined" color="primary">View</Button>
                    </Box>
                  </Paper>
                  
                  {/* Sample tool card */}
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderLeft: '4px solid #1e3c72'
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Carpenter's Square
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Provides accurate 90° angles for framing and layout work. Made from durable aluminum for outdoor use.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Chip size="small" label="Measuring Tool" sx={{ bgcolor: '#e3f2fd', color: '#1e3c72' }} />
                      <Button size="small" variant="outlined" color="primary">View</Button>
                    </Box>
                  </Paper>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box sx={{ py: 8, bgcolor: 'rgba(63, 81, 181, 0.04)' }}>
        <Container>
          <Typography
            variant="h2"
            textAlign="center"
            sx={{
              mb: 6,
              fontWeight: 'bold',
              color: '#1e3c72'
            }}
          >
            Create Your Project with DIYMind AI Experts
          </Typography>
          
          <Box sx={{ maxWidth: 800, mx: 'auto', mb: 6 }}>
            <Stepper alternativeLabel sx={{ mb: 5 }}>
              <Step active>
                <StepLabel>Choose an Expert</StepLabel>
              </Step>
              <Step active>
                <StepLabel>Define Your Project</StepLabel>
              </Step>
              <Step active>
                <StepLabel>Q&A Session</StepLabel>
              </Step>
              <Step active>
                <StepLabel>Get Blueprint & Chat</StepLabel>
              </Step>
            </Stepper>
          </Box>
          
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Paper
                  elevation={4}
                  sx={{
                    p: 4,
                    borderRadius: 2,
                    height: '100%',
                    background: 'white'
                  }}
                >
                  <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PersonIcon color="primary" />
                    1. Select Your DIYMind Expert
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Choose from specialized personas like DIY Mind or Restoration Artist based on your project needs.
                  </Typography>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <DescriptionIcon color="primary" />
                    2. Define Your Project & Answer Questions
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Describe your project and complete a Q&A session with your chosen expert to build your blueprint.
                  </Typography>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ChatIcon color="primary" />
                    3. Access Your Expert Chatbot
                  </Typography>
                  <Typography variant="body1">
                    Once your blueprint is complete, ask follow-up questions about your specific project and get detailed responses from your expert.
                  </Typography>
                </Paper>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                sx={{
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Paper
                  elevation={6}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    width: '100%',
                    maxWidth: 450,
                    background: 'white',
                    border: '1px solid rgba(0,0,0,0.1)'
                  }}
                >
                  <Box sx={{ 
                    p: 2, 
                    mb: 2, 
                    borderRadius: 2, 
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <BuildIcon />
                      <Typography variant="h6">DIYMind</Typography>
                    </Box>
                    <Typography variant="caption">YOUR EXPERT</Typography>
                  </Box>
                  
                  <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                    Project: Patio Table Construction
                  </Typography>
                  
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, mb: 2 }}>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      Question:
                    </Typography>
                    <Typography variant="body1">
                      What type of wood should I use considering my coastal location?
                    </Typography>
                  </Box>
                  
                  <Box sx={{ p: 2, bgcolor: '#e8f4fd', borderRadius: 2, mb: 3 }}>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      DIYMind:
                    </Typography>
                    <Typography variant="body1">
                      For your coastal patio table, I recommend cedar or teak which naturally resist salt air and moisture. Based on your $300 budget mentioned in your blueprint, cedar would be your best option. Make sure to seal it with a marine-grade finish for extra protection.
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/personal-projects')}
                      startIcon={<PersonIcon />}
                      sx={{
                        background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
                      }}
                    >
                      Start with DIYMind
                    </Button>
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box sx={{ py: 8 }}>
        <Container>
          <Paper
            elevation={3}
            sx={{
              p: 6,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
              color: 'white',
              textAlign: 'center'
            }}
          >
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
              Ready to Start Your DIY Journey with DIYMind?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Get expert guidance every step of the way with our specialized AI personas
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/projects')}
                startIcon={<SearchIcon />}
                sx={{
                  bgcolor: 'white',
                  color: '#1e3c72',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)',
                  }
                }}
              >
                Start Your Project
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/personal-projects')}
                startIcon={<PersonIcon />}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.9)',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  }
                }}
              >
                Choose Your Expert
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/tools')}
                startIcon={<HandymanIcon />}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.9)',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  }
                }}
              >
                Find DIY Tools
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
      
      {/* Footer with brand mention and description */}
      <Box sx={{ py: 4, bgcolor: '#f8f9fa', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
        <Container>
          <Box sx={{ maxWidth: 800, mx: 'auto', mb: 3 }}>
            <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 2 }}>
              DIYMind is an AI platform for DIYers, offering personalized video search, expert chatbots, and step-by-step project guidance.
            </Typography>
          </Box>
          <Typography variant="body2" textAlign="center" color="text.secondary">
            © 2025 DIYMind. All rights reserved. The AI-powered platform for smarter DIY projects.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;